import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '../../components/dashboard/DashboardIcons';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import { canViewFinance } from '../../config/organization';
import { errMsg } from './finance/shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const TYPE_TABS = [
  { key: 'all', label: 'الجميع' },
  { key: 'admin', label: 'الإداريون' },
  { key: 'coach', label: 'الطاقم التدريبي' },
  { key: 'medical', label: 'الطاقم الطبي' },
  { key: 'security', label: 'الأمن والحراسة' },
  { key: 'services', label: 'الخدمات العامة' },
];
const TYPE_LABELS = { admin: 'إداري', coach: 'مدرب', medical: 'طبي', security: 'أمن', services: 'خدمات' };
const money = (n) => new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => (d ? new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { dateStyle: 'medium' }).format(new Date(d)) : '—');

const EMPTY_FORM = { full_name: '', employee_type: 'admin', position: '', department: '', email: '', phone: '', national_id: '', nationality: '', salary: '', allowances: '', hire_date: '', contract_type: '', contract_start_date: '', contract_end_date: '', manager_name: '', address: '', notes: '', extra_clauses_text: '' };

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
  </label>
);
const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-teal-400 focus:outline-none';

const EmployeeFormModal = ({ open, initial, onClose, onSaved, showSalary }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm(initial?.id ? { ...EMPTY_FORM, ...initial, extra_clauses_text: Array.isArray(initial.extra_contract_clauses) ? initial.extra_contract_clauses.join('\n') : '' } : EMPTY_FORM);
      setError(null);
    }
  }, [open, initial]);
  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.position || !form.hire_date || !form.salary) { setError('يرجى تعبئة الحقول الإلزامية: الاسم، المسمى الوظيفي، الراتب، تاريخ الانضمام'); return; }
    setSaving(true); setError(null);
    const payload = { ...form, salary: parseFloat(form.salary), allowances: form.allowances ? parseFloat(form.allowances) : 0 };
    const clauses = (form.extra_clauses_text || '').split('\n').map((l) => l.trim()).filter(Boolean);
    payload.extra_contract_clauses = clauses.length ? clauses : null;
    delete payload.extra_clauses_text;
    ['contract_start_date', 'contract_end_date', 'department', 'email', 'phone', 'national_id', 'nationality', 'contract_type', 'manager_name', 'address', 'notes'].forEach((k) => { if (!payload[k]) delete payload[k]; });
    try {
      if (isEdit) await axios.put(`${API}/employees/${initial.id}`, payload, auth());
      else await axios.post(`${API}/employees`, payload, auth());
      onSaved();
    } catch (err) {
      setError(errMsg(err, 'فشل الحفظ، تحقق من البيانات'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="employee-form-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="mb-4 text-base font-bold text-slate-900">{isEdit ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد'}</h3>
        {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="employee-form-error">{error}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="الاسم الكامل" required><input className={inputCls} value={form.full_name} onChange={set('full_name')} data-testid="emp-input-name" /></Field>
          <Field label="نوع الموظف" required>
            <select className={inputCls} value={form.employee_type} onChange={set('employee_type')} data-testid="emp-input-type">
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="المسمى الوظيفي" required><input className={inputCls} value={form.position} onChange={set('position')} data-testid="emp-input-position" /></Field>
          <Field label="القسم"><input className={inputCls} value={form.department || ''} onChange={set('department')} /></Field>
          <Field label="الجوال"><input className={inputCls} dir="ltr" value={form.phone || ''} onChange={set('phone')} data-testid="emp-input-phone" /></Field>
          <Field label="البريد الإلكتروني"><input className={inputCls} dir="ltr" type="email" value={form.email || ''} onChange={set('email')} /></Field>
          <Field label="رقم الهوية"><input className={inputCls} dir="ltr" value={form.national_id || ''} onChange={set('national_id')} /></Field>
          <Field label="الجنسية"><input className={inputCls} value={form.nationality || ''} onChange={set('nationality')} /></Field>
          {showSalary && <Field label="الراتب الأساسي (ر.س)" required><input className={inputCls} dir="ltr" type="number" min="1" value={form.salary} onChange={set('salary')} data-testid="emp-input-salary" /></Field>}
          {showSalary && <Field label="البدلات (ر.س)"><input className={inputCls} dir="ltr" type="number" min="0" value={form.allowances || ''} onChange={set('allowances')} /></Field>}
          <Field label="تاريخ الانضمام" required><input className={inputCls} type="date" value={form.hire_date || ''} onChange={set('hire_date')} data-testid="emp-input-hire-date" /></Field>
          <Field label="نوع العقد">
            <select className={inputCls} value={form.contract_type || ''} onChange={set('contract_type')}>
              <option value="">—</option><option value="full_time">دوام كامل</option><option value="part_time">دوام جزئي</option><option value="seasonal">موسمي</option>
            </select>
          </Field>
          <Field label="بداية العقد"><input className={inputCls} type="date" value={form.contract_start_date || ''} onChange={set('contract_start_date')} /></Field>
          <Field label="نهاية العقد"><input className={inputCls} type="date" value={form.contract_end_date || ''} onChange={set('contract_end_date')} /></Field>
          <Field label="المدير المباشر"><input className={inputCls} value={form.manager_name || ''} onChange={set('manager_name')} /></Field>
          <Field label="ملاحظات"><input className={inputCls} value={form.notes || ''} onChange={set('notes')} /></Field>
          <div className="col-span-full">
            <Field label="بنود إضافية للعقد (كل بند في سطر مستقل)">
              <textarea rows={3} className={inputCls} placeholder={'مثال:\nيلتزم الموظف بالحضور في المواعيد المحددة من الإدارة.\nيحق للطرف الأول تعديل مكان العمل حسب الحاجة.'} value={form.extra_clauses_text || ''} onChange={set('extra_clauses_text')} data-testid="emp-input-extra-clauses" />
            </Field>
          </div>
          <div className="col-span-full mt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50" data-testid="emp-form-cancel">إلغاء</button>
            <button type="submit" disabled={saving} className="rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e] disabled:opacity-50" data-testid="emp-form-submit">
              {saving ? 'جارٍ الحفظ...' : isEdit ? 'حفظ التعديلات' : 'إضافة الموظف'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// بطاقة الموظف القابلة للطباعة
const EmployeeCardModal = ({ employee, club, onClose }) => {
  const [orientation, setOrientation] = useState('landscape');
  useEffect(() => { if (employee) setOrientation('landscape'); }, [employee]);
  if (!employee) return null;
  const logoUrl = club?.logo_file_id ? `${API}/files/${club.logo_file_id}` : null;
  const portrait = orientation === 'portrait';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="employee-card-modal">
      <div className="absolute inset-0 bg-black/50 print:hidden" onClick={onClose} />
      <div className="relative w-full max-w-md">
        <div className="mb-3 flex justify-center gap-2 print:hidden" data-testid="employee-card-orientation-toggle">
          <button onClick={() => setOrientation('landscape')} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${!portrait ? 'bg-[#16233b] text-white' : 'bg-white text-slate-600 border border-slate-200'}`} data-testid="employee-card-orientation-landscape">عرضي</button>
          <button onClick={() => setOrientation('portrait')} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${portrait ? 'bg-[#16233b] text-white' : 'bg-white text-slate-600 border border-slate-200'}`} data-testid="employee-card-orientation-portrait">طولي</button>
        </div>
        {portrait ? (
          <div id="employee-print-card" className={`emp-card-print emp-card-portrait mx-auto overflow-hidden rounded-2xl bg-white shadow-2xl`} data-testid="employee-print-card">
            <div className="bg-[#16233b] px-5 py-5 text-center">
              {logoUrl && <img src={logoUrl} alt="شعار" className="mx-auto h-12 w-12 rounded-lg bg-white/10 object-cover" />}
              <div className="mt-2 text-sm font-bold text-white">{club?.name || club?.club_name || 'المنشأة'}</div>
              <div className="text-[11px] text-slate-300">بطاقة موظف</div>
            </div>
            <div className="flex flex-col items-center gap-2 px-5 py-5" dir="rtl">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-slate-100 text-3xl font-bold text-[#16233b]">
                {employee.full_name?.trim().charAt(0)}
              </div>
              <div className="text-base font-bold text-slate-900">{employee.full_name}</div>
              <div className="text-sm text-slate-600">{employee.position}</div>
              <div className="text-xs text-slate-500">{TYPE_LABELS[employee.employee_type] || employee.employee_type}{employee.department ? ` • ${employee.department}` : ''}</div>
            </div>
            <div className="space-y-1.5 border-t border-slate-100 px-5 py-4 text-center text-xs">
              <div><span className="text-slate-400">الرقم الوظيفي: </span><span className="font-bold text-slate-800" dir="ltr">{employee.employee_number || '—'}</span></div>
              <div><span className="text-slate-400">تاريخ الانضمام: </span><span className="font-semibold text-slate-700">{fmtDate(employee.hire_date)}</span></div>
              {employee.phone && <div><span className="text-slate-400">الجوال: </span><span className="font-semibold text-slate-700" dir="ltr">{employee.phone}</span></div>}
              <div><span className="text-slate-400">الحالة: </span><span className={`font-semibold ${employee.is_active ? 'text-teal-700' : 'text-red-600'}`}>{employee.is_active ? 'على رأس العمل' : 'موقوف'}</span></div>
            </div>
            <div className="flex flex-col items-center gap-1 border-t border-slate-100 px-5 py-4">
              <QRCodeSVG value={`${window.location.origin}/verify/employee/${employee.id}`} size={100} level="M" data-testid="employee-card-qr" />
              <div className="text-[10px] text-slate-400">مسح الرمز للتحقق من انتساب الموظف</div>
            </div>
            <div className="bg-slate-50 px-5 py-2 text-center text-[10px] text-slate-400">هذه البطاقة صادرة إلكترونياً من نظام إدارة المنشأة</div>
          </div>
        ) : (
          <div id="employee-print-card" className="emp-card-print overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="employee-print-card">
            <div className="flex items-center gap-3 bg-[#16233b] px-5 py-4">
              {logoUrl && <img src={logoUrl} alt="شعار" className="h-12 w-12 rounded-lg bg-white/10 object-cover" />}
              <div>
                <div className="text-sm font-bold text-white">{club?.name || club?.club_name || 'المنشأة'}</div>
                <div className="text-[11px] text-slate-300">بطاقة موظف</div>
              </div>
            </div>
            <div className="flex gap-4 px-5 py-5" dir="rtl">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-2xl font-bold text-[#16233b]">
                {employee.full_name?.trim().charAt(0)}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="text-base font-bold text-slate-900">{employee.full_name}</div>
                <div className="text-sm text-slate-600">{employee.position}</div>
                <div className="text-xs text-slate-500">{TYPE_LABELS[employee.employee_type] || employee.employee_type}{employee.department ? ` • ${employee.department}` : ''}</div>
              </div>
              <div className="flex shrink-0 flex-col items-center gap-1">
                <QRCodeSVG value={`${window.location.origin}/verify/employee/${employee.id}`} size={64} level="M" data-testid="employee-card-qr" />
                <div className="text-[8px] text-slate-400">تحقق</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 border-t border-slate-100 px-5 py-4 text-xs">
              <div><span className="text-slate-400">الرقم الوظيفي:</span> <span className="font-bold text-slate-800" dir="ltr">{employee.employee_number || '—'}</span></div>
              <div><span className="text-slate-400">تاريخ الانضمام:</span> <span className="font-semibold text-slate-700">{fmtDate(employee.hire_date)}</span></div>
              {employee.phone && <div><span className="text-slate-400">الجوال:</span> <span className="font-semibold text-slate-700" dir="ltr">{employee.phone}</span></div>}
              <div><span className="text-slate-400">الحالة:</span> <span className={`font-semibold ${employee.is_active ? 'text-teal-700' : 'text-red-600'}`}>{employee.is_active ? 'على رأس العمل' : 'موقوف'}</span></div>
            </div>
            <div className="bg-slate-50 px-5 py-2 text-center text-[10px] text-slate-400">هذه البطاقة صادرة إلكترونياً من نظام إدارة المنشأة</div>
          </div>
        )}
        <div className="mt-3 flex justify-center gap-2 print:hidden">
          <button onClick={() => window.print()} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e]" data-testid="employee-card-print-btn">
            <Icon name="document" className="w-4 h-4" /> طباعة البطاقة
          </button>
          <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600" data-testid="employee-card-close-btn">إغلاق</button>
        </div>
      </div>
      <style>{`
        .emp-card-portrait { width: 300px; }
        @media print {
          body * { visibility: hidden !important; }
          .emp-card-print, .emp-card-print * { visibility: visible !important; }
          .emp-card-print { position: fixed !important; top: 20px; right: 0; left: 0; margin: auto; width: 420px; box-shadow: none !important; }
          .emp-card-print.emp-card-portrait { width: 300px !important; }
        }
      `}</style>
    </div>
  );
};

// عقد العمل القابل للطباعة
const CONTRACT_TYPES = { full_time: 'دوام كامل', part_time: 'دوام جزئي', seasonal: 'موسمي' };

const buildDefaultClauses = (employee, showSalary) => {
  const totalSalary = (employee.salary || 0) + (employee.allowances || 0);
  const clauses = [
    `يعمل الطرف الثاني لدى الطرف الأول بوظيفة «${employee.position}»${employee.contract_type ? ` بنظام ${CONTRACT_TYPES[employee.contract_type] || employee.contract_type}` : ''}.`,
    `مدة العقد من ${fmtDate(employee.contract_start_date || employee.hire_date)}${employee.contract_end_date ? ` حتى ${fmtDate(employee.contract_end_date)}` : ' (غير محددة المدة)'}.`,
  ];
  if (showSalary) {
    clauses.push(`يتقاضى الطرف الثاني راتباً شهرياً إجمالياً قدره ${money(totalSalary)} (${money(employee.salary || 0)} راتب أساسي${employee.allowances ? ` + ${money(employee.allowances)} بدلات` : ''}).`);
  }
  clauses.push('يلتزم الطرف الثاني بأنظمة ولوائح الطرف الأول الداخلية وبأنظمة العمل المعمول بها.');
  clauses.push('ما لم يرد فيه نص في هذا العقد تُطبق عليه أحكام نظام العمل السعودي.');
  if (Array.isArray(employee.extra_contract_clauses)) clauses.push(...employee.extra_contract_clauses);
  return clauses;
};

// تحرير بنود العقد قبل الطباعة
const ClauseEditorModal = ({ employee, showSalary, onClose, onProceed }) => {
  const [clauses, setClauses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!employee) return;
    const initial = Array.isArray(employee.contract_clauses) && employee.contract_clauses.length
      ? employee.contract_clauses
      : buildDefaultClauses(employee, showSalary);
    setClauses(initial);
    setError(null);
  }, [employee, showSalary]);

  if (!employee) return null;

  const updateClause = (i, val) => setClauses((c) => c.map((x, idx) => (idx === i ? val : x)));
  const removeClause = (i) => setClauses((c) => c.filter((_, idx) => idx !== i));
  const addClause = () => setClauses((c) => [...c, '']);
  const move = (i, dir) => setClauses((c) => {
    const next = [...c];
    const j = i + dir;
    if (j < 0 || j >= next.length) return next;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const proceed = async () => {
    const final = clauses.map((c) => c.trim()).filter(Boolean);
    if (final.length === 0) { setError('يجب أن يحتوي العقد على بند واحد على الأقل'); return; }
    setSaving(true); setError(null);
    try {
      await axios.put(`${API}/employees/${employee.id}`, { contract_clauses: final }, auth());
      onProceed(final);
    } catch (e2) { setError(errMsg(e2, 'فشل حفظ بنود العقد')); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="clause-editor-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="mb-1 text-base font-bold text-slate-900">تحرير بنود عقد «{employee.full_name}»</h3>
        <p className="mb-4 text-xs text-slate-500">راجع وعدّل بنود العقد (الأساسية والإضافية) قبل المعاينة والطباعة. يمكنك حذف أي بند أو إضافة بند جديد أو تغيير ترتيبها.</p>
        {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="clause-editor-error">{error}</div>}
        <div className="space-y-2" data-testid="clause-editor-list">
          {clauses.map((c, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid="clause-editor-row">
              <span className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#16233b] text-xs font-bold text-white">{i + 1}</span>
              <textarea
                rows={2}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-teal-400 focus:outline-none"
                value={c}
                onChange={(e) => updateClause(i, e.target.value)}
                data-testid={`clause-editor-input-${i}`}
              />
              <div className="flex shrink-0 flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-slate-400 hover:bg-slate-200 disabled:opacity-30" data-testid={`clause-move-up-${i}`}><Icon name="undo" className="h-3.5 w-3.5 rotate-90" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === clauses.length - 1} className="rounded p-1 text-slate-400 hover:bg-slate-200 disabled:opacity-30" data-testid={`clause-move-down-${i}`}><Icon name="redo" className="h-3.5 w-3.5 rotate-90" /></button>
                <button type="button" onClick={() => removeClause(i)} className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600" data-testid={`clause-remove-${i}`}><Icon name="trash" className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addClause} className="mt-3 flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700" data-testid="clause-add-btn">
          <Icon name="plus" className="h-4 w-4" /> إضافة بند جديد
        </button>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600" data-testid="clause-editor-cancel">إلغاء</button>
          <button type="button" onClick={proceed} disabled={saving} className="rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e] disabled:opacity-50" data-testid="clause-editor-proceed">
            {saving ? 'جارٍ الحفظ...' : 'معاينة وطباعة'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContractModal = ({ employee, club, clauses, onClose }) => {
  React.useEffect(() => {
    if (!employee) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [employee, onClose]);
  if (!employee) return null;
  const logoUrl = club?.logo_file_id ? `${API}/files/${club.logo_file_id}` : null;
  const clubName = club?.name || club?.club_name || 'المنشأة الرياضية';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4" data-testid="contract-modal">
      <div className="absolute inset-0 bg-black/50 print:hidden" onClick={onClose} />
      <div className="relative w-full max-w-2xl">
        <div className="contract-print max-h-[75vh] overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl" dir="rtl" data-testid="contract-doc">
          <div className="flex items-center justify-between border-b-2 border-[#16233b] pb-4">
            <div className="flex items-center gap-3">
              {logoUrl && <img src={logoUrl} alt="شعار" className="h-14 w-14 rounded-lg object-contain" />}
              <div>
                <div className="text-lg font-bold text-[#16233b]">{clubName}</div>
                {club?.city && <div className="text-xs text-slate-500">{club.city}</div>}
              </div>
            </div>
            <div className="text-left">
              <div className="text-base font-bold text-[#16233b]">عقد عمل</div>
              <div className="text-xs text-slate-500">التاريخ: {fmtDate(new Date())}</div>
            </div>
          </div>
          <p className="mt-4 text-sm leading-6 text-slate-700">
            تم بعون الله إبرام هذا العقد بين <b>{clubName}</b> (ويشار إليها فيما بعد بـ«الطرف الأول») وبين
            الموضحة بياناته أدناه (ويشار إليه فيما بعد بـ«الطرف الثاني») على البنود والشروط التالية:
          </p>
          <div className="mt-4 rounded-lg border border-slate-200">
            <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700">بيانات الطرف الثاني (الموظف)</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-3 text-sm">
              <div><span className="text-slate-400">الاسم: </span><b className="text-slate-800">{employee.full_name}</b></div>
              <div><span className="text-slate-400">الرقم الوظيفي: </span><b className="text-slate-800" dir="ltr">{employee.employee_number || '—'}</b></div>
              <div><span className="text-slate-400">رقم الهوية: </span><span className="font-semibold text-slate-700" dir="ltr">{employee.national_id || '—'}</span></div>
              <div><span className="text-slate-400">الجنسية: </span><span className="font-semibold text-slate-700">{employee.nationality || '—'}</span></div>
              <div><span className="text-slate-400">المسمى الوظيفي: </span><span className="font-semibold text-slate-700">{employee.position}</span></div>
              <div><span className="text-slate-400">القسم: </span><span className="font-semibold text-slate-700">{employee.department || TYPE_LABELS[employee.employee_type] || '—'}</span></div>
              <div><span className="text-slate-400">الجوال: </span><span className="font-semibold text-slate-700" dir="ltr">{employee.phone || '—'}</span></div>
              <div><span className="text-slate-400">العنوان: </span><span className="font-semibold text-slate-700">{employee.address || '—'}</span></div>
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-slate-200">
            <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700">بنود العقد</div>
            <div className="space-y-2 px-4 py-3 text-sm leading-6 text-slate-700" data-testid="contract-clauses">
              {(clauses || []).map((clause, idx) => (
                <p key={idx} data-testid={`contract-clause-${idx}`}><b>البند {idx + 1}:</b> {clause}</p>
              ))}
            </div>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-8 text-center text-sm">
            <div><div className="font-bold text-slate-800">الطرف الأول</div><div className="text-xs text-slate-500">{clubName}</div><div className="mt-10 border-t border-slate-300 pt-1 text-xs text-slate-400">التوقيع والختم</div></div>
            <div><div className="font-bold text-slate-800">الطرف الثاني</div><div className="text-xs text-slate-500">{employee.full_name}</div><div className="mt-10 border-t border-slate-300 pt-1 text-xs text-slate-400">التوقيع</div></div>
          </div>
          <div className="mt-6 border-t border-slate-100 pt-2 text-center text-[10px] text-slate-400">صدر هذا العقد إلكترونياً من نظام إدارة {clubName}</div>
        </div>
        <div className="mt-3 flex justify-center gap-2 print:hidden">
          <button onClick={() => window.print()} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e]" data-testid="contract-print-btn">
            <Icon name="document" className="w-4 h-4" /> طباعة العقد
          </button>
          <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600" data-testid="contract-close-btn">إغلاق</button>
        </div>
      </div>
      <style>{`@media print { body * { visibility: hidden !important; } .contract-print, .contract-print * { visibility: visible !important; } .contract-print { position: fixed !important; inset: 0; max-height: none !important; overflow: visible !important; border-radius: 0 !important; box-shadow: none !important; } }`}</style>
    </div>
  );
};

export default function EmployeesPage({ initialType = 'all' }) {
  const { user, club, selectedSeasonId } = useOutletContext() || {};
  const seasonQS = selectedSeasonId && selectedSeasonId !== 'all' ? `?season_id=${selectedSeasonId}` : '';
  const [params] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialType);
  useEffect(() => { setTab(initialType); }, [initialType]);
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cardEmp, setCardEmp] = useState(null);
  const [clauseEditEmp, setClauseEditEmp] = useState(null);
  const [contractEmp, setContractEmp] = useState(null);
  const [contractClauses, setContractClauses] = useState([]);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [toast, setToast] = useState(null);

  const showSalary = canViewFinance(user);
  const canManage = user?.is_super_admin || ['admin', 'manager'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees${seasonQS}`, auth());
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch { setEmployees([]); }
    setLoading(false);
  }, [seasonQS]);
  useEffect(() => { load(); }, [load]);

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const toggleActive = async (emp) => {
    try {
      await axios.put(`${API}/employees/${emp.id}`, { is_active: !emp.is_active }, auth());
      notify(emp.is_active ? 'تم تعطيل الموظف (لم يُحذف)' : 'تمت إعادة تفعيل الموظف');
      setConfirmToggle(null);
      load();
    } catch { notify('فشلت العملية'); }
  };

  const filtered = employees
    .filter((e) => tab === 'all' || e.employee_type === tab)
    .filter((e) => !search.trim() || e.full_name?.includes(search.trim()) || e.position?.includes(search.trim()) || (e.employee_number || '').includes(search.trim()));

  const now = new Date();
  const soon = new Date(); soon.setDate(soon.getDate() + 60);
  const expiring = employees.filter((e) => e.is_active && e.contract_end_date && new Date(e.contract_end_date) >= now && new Date(e.contract_end_date) <= soon);
  const activeCount = employees.filter((e) => e.is_active).length;

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="employees-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg" data-testid="employees-toast">{toast}</div>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">إدارة الموظفين</h2>
          <p className="text-sm text-slate-500">جميع منسوبي المنشأة: إداريون، مدربون، طاقم طبي، أمن وخدمات</p>
        </div>
        {canManage && (
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e]" data-testid="add-employee-btn">
            <Icon name="plus" className="w-4 h-4" /> إضافة موظف
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي الموظفين</div><div className="text-xl font-bold text-slate-900" data-testid="emp-stat-total">{employees.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">على رأس العمل</div><div className="text-xl font-bold text-teal-700">{activeCount}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">عقود تنتهي خلال 60 يوماً</div><div className={`text-xl font-bold ${expiring.length ? 'text-amber-600' : 'text-slate-900'}`} data-testid="emp-stat-expiring">{expiring.length}</div></div>
        {showSalary && <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي الرواتب الشهرية</div><div className="text-xl font-bold text-slate-900" dir="rtl" data-testid="emp-stat-salaries">{money(employees.filter((e) => e.is_active).reduce((s, e) => s + (e.salary || 0) + (e.allowances || 0), 0))}</div></div>}
      </div>

      {expiring.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="contracts-expiring-alert">
          <Icon name="warning" className="w-4 h-4" />
          عقود قاربت على الانتهاء: {expiring.map((e) => e.full_name).join('، ')}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex flex-wrap gap-1" data-testid="employee-type-tabs">
            {TYPE_TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`emp-tab-${t.key}`}
                className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${tab === t.key ? 'bg-[#16233b] text-white font-semibold' : 'text-slate-600 hover:bg-slate-100'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="mr-auto flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم أو المسمى أو الرقم..." className="w-44 bg-transparent text-sm outline-none" data-testid="employee-search-input" />
          </div>
        </div>

        {loading ? (
          <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-6"><EmptyState icon="briefcase" text={employees.length === 0 ? 'لا يوجد موظفون مسجلون بعد — ابدأ بإضافة أول موظف' : 'لا توجد نتائج مطابقة للبحث أو التصنيف'} testId="employees-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">الموظف</th>
                  <th className="px-4 py-2.5 font-semibold">الرقم الوظيفي</th>
                  <th className="hidden px-4 py-2.5 font-semibold md:table-cell">النوع</th>
                  <th className="hidden px-4 py-2.5 font-semibold lg:table-cell">تاريخ الانضمام</th>
                  {showSalary && <th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الراتب</th>}
                  <th className="px-4 py-2.5 font-semibold">الحالة</th>
                  <th className="px-4 py-2.5 font-semibold">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => (
                  <tr key={emp.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="employee-row">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-800">{emp.full_name}</div>
                      <div className="text-xs text-slate-500">{emp.position}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600" dir="ltr">{emp.employee_number || '—'}</td>
                    <td className="hidden px-4 py-2.5 md:table-cell"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{TYPE_LABELS[emp.employee_type] || emp.employee_type}</span></td>
                    <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell">{fmtDate(emp.hire_date)}</td>
                    {showSalary && <td className="hidden px-4 py-2.5 text-slate-700 lg:table-cell" dir="rtl">{money((emp.salary || 0) + (emp.allowances || 0))}</td>}
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${emp.is_active ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`}>{emp.is_active ? 'نشط' : 'موقوف'}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setCardEmp(emp)} title="طباعة بطاقة الموظف" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#16233b]" data-testid="employee-card-btn"><Icon name="document" className="w-4 h-4" /></button>
                        {canManage && <button onClick={() => setClauseEditEmp(emp)} title="عقد العمل" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#16233b]" data-testid="employee-contract-btn"><Icon name="briefcase" className="w-4 h-4" /></button>}
                        {canManage && <button onClick={() => { setEditing(emp); setFormOpen(true); }} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="employee-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>}
                        {canManage && <button onClick={() => setConfirmToggle(emp)} title={emp.is_active ? 'تعطيل' : 'تفعيل'} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="employee-toggle-btn"><Icon name={emp.is_active ? 'x' : 'check'} className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EmployeeFormModal open={formOpen} initial={editing} showSalary={showSalary} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); notify('تم الحفظ بنجاح'); load(); }} />
      <EmployeeCardModal employee={cardEmp} club={club} onClose={() => setCardEmp(null)} />
      <ClauseEditorModal
        employee={clauseEditEmp}
        showSalary={showSalary}
        onClose={() => setClauseEditEmp(null)}
        onProceed={(finalClauses) => {
          setContractClauses(finalClauses);
          setContractEmp(clauseEditEmp);
          setClauseEditEmp(null);
          load();
        }}
      />
      <ContractModal employee={contractEmp} club={club} clauses={contractClauses} onClose={() => setContractEmp(null)} />

      {confirmToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="employee-confirm-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmToggle(null)} />
          <div className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl">
            <h4 className="text-sm font-bold text-slate-900">{confirmToggle.is_active ? 'تعطيل الموظف' : 'إعادة تفعيل الموظف'}</h4>
            <p className="mt-1 text-sm text-slate-500">
              {confirmToggle.is_active
                ? `سيتم تعطيل «${confirmToggle.full_name}» دون حذف سجلاته أو بياناته المرتبطة.`
                : `سيعود «${confirmToggle.full_name}» إلى قائمة الموظفين النشطين.`}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirmToggle(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <button onClick={() => toggleActive(confirmToggle)} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${confirmToggle.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-600 hover:bg-teal-700'}`} data-testid="employee-confirm-btn">تأكيد</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
